import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || '';

// Code of Conduct accept/decline modal. Opened when the server blocks chat with a
// "please accept the Code of Conduct" notice, or on login when re-accept is required.
const CodeOfConductModal = ({ onClose, onAccepted }) => {
  const [data, setData] = useState(null);   // { version, rules }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/coc`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to load');
      setData(body);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/coc/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to record acceptance');
      toast.success('Code of Conduct accepted — chat is now unlocked.', { duration: 5000 });
      if (onAccepted) onAccepted();
      onClose();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 9700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' };
  const panel = { width: 'min(560px, 96vw)', maxHeight: '90vh', background: 'linear-gradient(180deg,#161a2b,#0f1220)', border: '1px solid #2a2f4a', borderRadius: '12px', display: 'flex', flexDirection: 'column', color: '#dcdce6', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.9rem 1.1rem', borderBottom: '1px solid #23283f' }}>
          <span style={{ fontSize: '1.3rem' }}>📜</span>
          <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#f0d98c' }}>Code of Conduct</h2>
          {data?.version && <span style={{ fontSize: '0.7rem', color: '#7a7f9a' }}>v{data.version}</span>}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.08)', border: '1px solid #333', color: '#ccc', width: 30, height: 30, borderRadius: '50%', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0.9rem 1.1rem' }}>
          <p style={{ marginTop: 0, fontSize: '0.82rem', color: '#b9bdd0' }}>
            Please review and accept our Code of Conduct to use chat. It keeps the realm friendly for everyone.
          </p>
          {error && <div style={{ background: 'rgba(231,76,60,0.15)', color: '#ff9b8f', padding: '0.5rem 0.7rem', borderRadius: 6, fontSize: '0.8rem', marginBottom: '0.6rem' }}>⚠️ {error}</div>}
          {!data && !error && <div style={{ color: '#777' }}>Loading…</div>}
          {data?.rules && data.rules.map((r, i) => (
            <div key={i} style={{ marginBottom: '0.7rem' }}>
              <div style={{ fontWeight: 'bold', color: '#cfd3e6', fontSize: '0.85rem' }}>{i + 1}. {r.heading}</div>
              <div style={{ fontSize: '0.78rem', color: '#9aa0bd', marginTop: 2 }}>{r.body}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', padding: '0.8rem 1.1rem', borderTop: '1px solid #23283f' }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: '0.5rem 1rem', borderRadius: 6, border: '1px solid #3a3f57', background: 'transparent', color: '#9aa0bd', cursor: busy ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}>
            Decline
          </button>
          <button onClick={accept} disabled={busy || !data}
            style={{ padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: '#2ecc71', color: '#0d1a10', fontWeight: 'bold', cursor: (busy || !data) ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}>
            {busy ? 'Saving…' : 'Accept'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeOfConductModal;
